import { useState, useEffect, useLayoutEffect, useCallback, type ReactNode } from 'react'
import { PALETTES } from './palettes'
import { ThemeContext } from './themeContextState'
import {
  HIGH_CONTRAST_STORAGE_KEY,
  applyHighContrastDocumentFlag,
  enhancePaletteForHighContrast,
  readHighContrastPreference,
} from '@/utils/highContrastPalette'

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [paletteId, setPaletteId] = useState<string>(() => {
    return localStorage.getItem('fitutogether-palette') ?? 'white'
  })

  const [highContrast, setHighContrast] = useState(readHighContrastPreference)

  const basePalette = PALETTES.find((p) => p.id === paletteId) ?? PALETTES[0]
  const palette = highContrast ? enhancePaletteForHighContrast(basePalette) : basePalette

  useLayoutEffect(() => {
    applyHighContrastDocumentFlag(highContrast)
  }, [highContrast])

  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--page-bg', palette.bg)
    root.style.setProperty('--page-text', palette.text)
    root.style.setProperty('--page-ring', palette.ring)
    localStorage.setItem('fitutogether-palette', basePalette.id)
  }, [palette, basePalette.id])

  useEffect(() => {
    localStorage.setItem(HIGH_CONTRAST_STORAGE_KEY, String(highContrast))
  }, [highContrast])

  const setPalette = (id: string) => setPaletteId(id)

  const toggleHighContrast = useCallback(() => {
    setHighContrast((on) => !on)
  }, [])

  return (
    <ThemeContext.Provider value={{ palette, setPalette, highContrast, toggleHighContrast }}>
      {children}
    </ThemeContext.Provider>
  )
}
